/**
 * Sales Year — Pure Utility Functions
 *
 * No React or Dataverse dependencies. Safe to import in tests or Node scripts.
 */

/**
 * Pure function: compute the sales year YTD range.
 * startMonth is 1-based (1=Jan, 2=Feb, etc).
 * Returns start of sales year through end of throughMonth/throughYear.
 */
export function getSalesYtdRange(
  startMonth: number,
  throughYear: number,
  throughMonth: number,
): { start: Date; end: Date; salesYearLabel: string } {
  // Determine sales year start: if throughMonth is before startMonth,
  // the sales year started in the previous calendar year
  const salesStartYear = throughMonth + 1 >= startMonth ? throughYear : throughYear - 1;
  const start = new Date(salesStartYear, startMonth - 1, 1);
  const end = new Date(throughYear, throughMonth + 1, 0, 23, 59, 59, 999);
  const salesYearLabel = `SalesYear ${salesStartYear}`;
  return { start, end, salesYearLabel };
}

/**
 * Returns true if a date falls within the given sales year.
 * The sales year starting in `salesStartYear` runs from
 * startMonth of that year to (startMonth-1) of the next year.
 */
/**
 * Pure function: compute the full sales year range.
 * Returns start and end of the sales year containing the given month/year.
 */
export function getSalesYearRange(
  startMonth: number,
  throughYear: number,
  throughMonth: number,
): { start: Date; end: Date; salesYearLabel: string } {
  const salesStartYear = throughMonth + 1 >= startMonth ? throughYear : throughYear - 1;
  const start = new Date(salesStartYear, startMonth - 1, 1);
  const end = new Date(salesStartYear + 1, startMonth - 1, 0, 23, 59, 59, 999);
  const salesYearLabel = `SalesYear ${salesStartYear}`;
  return { start, end, salesYearLabel };
}

export function isInSalesYear(
  date: Date,
  startMonth: number,
  salesStartYear: number,
): boolean {
  const syStart = new Date(salesStartYear, startMonth - 1, 1);
  const syEnd = new Date(salesStartYear + 1, startMonth - 1, 0, 23, 59, 59, 999);
  return date >= syStart && date <= syEnd;
}

/**
 * Returns the sales year that contains the given date.
 * The sales year is labeled by its start year — e.g. with startMonth=2 (Feb),
 * a date in Jan 2026 falls in sales year 2025 (Feb 2025 – Jan 2026).
 */
export function getSalesYearForDate(date: Date, startMonth: number): number {
  return date.getMonth() + 1 >= startMonth ? date.getFullYear() : date.getFullYear() - 1;
}
